import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import { cssRaw, style } from 'typestyle';
import DashboardTheme from '../dashboard-theme';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';

export const controlsRow = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  // wrap only makes sense in row direction (lets groups wrap onto new rows); in column direction
  // (mobile) wrap creates new *columns* instead, which pushes the layout wider than the viewport.
  flexWrap: isMobile ? 'nowrap' : 'wrap',
  gap: 16,
  alignItems: isMobile ? 'stretch' : 'flex-end',
  marginBottom: 20,
});

export const controlGroup: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  // Flex items default to min-width:auto, which refuses to shrink below the content's natural
  // width (here, the unwrapped width of all preset/comparison chips) even though the chips
  // themselves wrap onto multiple lines — that forces the whole page wider on narrow screens.
  minWidth: 0,
};

export const controlLabel: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 700,
  color: DashboardTheme.navy800,
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const presetChips: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
};

export const chip = (active: boolean): CSSProperties => ({
  padding: '6px 14px',
  borderRadius: 999,
  cursor: 'pointer',
  fontSize: 12.5,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontWeight: active ? 700 : 500,
  border: `1px solid ${active ? DashboardTheme.navy800 : DashboardTheme.gray200}`,
  color: active ? DashboardTheme.white : DashboardTheme.navy700,
  background: active ? DashboardTheme.navy800 : DashboardTheme.white,
  whiteSpace: 'nowrap',
  transition: 'background-color .12s ease, border-color .12s ease',
});

export const dateRangeRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center',
};

export const calendar: CSSProperties = {
  fontWeight: 500,
  fontFamily: DashboardTheme.bodyFontFamily,
  padding: 16,
  border: '0px',
};

export const comparisonRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 8,
  overflow: 'hidden',
};

export const comparisonOption = (active: boolean): CSSProperties => ({
  padding: '8px 14px',
  cursor: 'pointer',
  fontSize: 12.5,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontWeight: active ? 700 : 500,
  color: active ? DashboardTheme.white : DashboardTheme.navy700,
  background: active ? DashboardTheme.navy800 : DashboardTheme.white,
  whiteSpace: 'nowrap',
  flex: '1 1 auto',
  textAlign: 'center',
  transition: 'background-color .12s ease',
});

export const deltaPositive: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: DashboardTheme.green600,
  display: 'block',
  marginTop: 4,
};

export const deltaNegative: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: DashboardTheme.red500,
  display: 'block',
  marginTop: 4,
};

export const deltaNeutral: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: DashboardTheme.gray500,
  display: 'block',
  marginTop: 4,
};

export const chartCard: CSSProperties = {
  padding: '18px 20px',
  background: DashboardTheme.white,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: DashboardTheme.radius,
  boxShadow: DashboardTheme.shadow,
  marginBottom: 20,
  minWidth: 0,
};

export const chartTitle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 700,
  color: DashboardTheme.navy800,
  marginBottom: 10,
  fontFamily: DashboardTheme.bodyFontFamily,
};

// A stable class (not just a style object) so the row-hover rule below can be scoped to it.
export const tableWrap = style({
  overflowX: 'auto',
  minWidth: 0,
  background: DashboardTheme.white,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 12,
  marginBottom: 20,
});

cssRaw(
  `
  .${tableWrap} tbody tr {
    transition: background-color .12s ease;
  }
  .${tableWrap} tbody tr:hover {
    background-color: ${DashboardTheme.gray50};
  }
  `,
);

export const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

export const tableHeaderCell: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: DashboardTheme.gray700,
  background: DashboardTheme.gray50,
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  whiteSpace: 'nowrap',
};

export const tableCell: CSSProperties = {
  padding: '10px 12px',
  fontSize: 13,
  fontWeight: 500,
  color: DashboardTheme.navy800,
  borderBottom: `1px solid ${DashboardTheme.gray100}`,
  whiteSpace: 'nowrap',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const exportRow: CSSProperties = {
  display: 'flex',
  gap: 12,
  marginBottom: 20,
  flexWrap: 'wrap',
  alignItems: 'center',
};

export const companySelect: CSSProperties = {
  padding: 8,
  paddingLeft: 12,
  paddingRight: 12,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 10,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13.5,
  color: DashboardTheme.navy800,
  height: 34,
  minWidth: 180,
  backgroundColor: DashboardTheme.white,
};

export const employeePresenceControlsRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  marginBottom: 14,
};

// The Employee presence panel's header: title on the left, its own CSV/PDF buttons on the right.
// The period itself comes from the tab-level selector — this panel deliberately has no date
// control of its own.
export const employeePresenceHeaderRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 14,
};

export const employeePresenceActions: CSSProperties = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
};

// Real <button>s rather than clickable <td>s: the row action has to be reachable by keyboard and
// announced as a control, which a table cell with role="button" is not.
export const employeeNameButton: NestedCSSProperties = {
  padding: 0,
  border: 'none',
  background: 'none',
  font: 'inherit',
  color: DashboardTheme.navy700,
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
  $nest: {
    '&:hover': { textDecoration: 'underline' },
  },
};

export const rowActionCell: CSSProperties = {
  textAlign: 'center',
  width: 44,
};

export const rowActionButton: NestedCSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'none',
  color: DashboardTheme.gray500,
  fontSize: 20,
  lineHeight: 1,
  cursor: 'pointer',
  $nest: {
    '&:hover': { color: DashboardTheme.navy700 },
  },
};

cssRaw(
  `
  .react-date-picker__calendar .react-calendar {
    width: 260px;
    border: none;
    font-family: ${DashboardTheme.bodyFontFamily};
    line-height: 1.4;
  }

  .react-date-picker__calendar .react-calendar__navigation {
    height: 32px;
    margin-bottom: 8px;
    display: flex;
  }

  .react-date-picker__calendar .react-calendar__navigation button {
    min-width: 32px;
    background: none;
    border-radius: 8px;
    color: ${DashboardTheme.navy800};
    font-weight: 700;
    font-size: 13.5px;
  }

  .react-date-picker__calendar .react-calendar__navigation button:disabled {
    color: ${DashboardTheme.gray300};
  }

  .react-date-picker__calendar .react-calendar__navigation button:enabled:hover,
  .react-date-picker__calendar .react-calendar__navigation button:enabled:focus {
    background-color: ${DashboardTheme.amber100};
    color: ${DashboardTheme.navy900};
  }

  .react-date-picker__calendar .react-calendar__tile {
    height: 40px;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: ${DashboardTheme.gray50};
    color: ${DashboardTheme.navy800};
    font-family: ${DashboardTheme.bodyFontFamily};
    font-size: 12.5px;
    font-weight: 600;
    transition: background-color .12s ease, color .12s ease;
  }

  .react-date-picker__calendar .react-calendar__tile:enabled:hover,
  .react-date-picker__calendar .react-calendar__tile:enabled:focus {
    background-color: ${DashboardTheme.amber100};
    color: ${DashboardTheme.navy900};
  }

  .react-date-picker__calendar .react-calendar__tile--now {
    box-shadow: inset 0 0 0 1.5px ${DashboardTheme.amber500};
  }

  .react-date-picker__calendar .react-calendar__tile--active,
  .react-date-picker__calendar .react-calendar__tile--active:enabled:hover {
    background: ${DashboardTheme.navy800};
    color: ${DashboardTheme.white};
  }

  .react-date-picker__calendar .react-calendar__tile:disabled {
    background: ${DashboardTheme.gray50};
    color: ${DashboardTheme.gray300};
  }

  .react-date-picker__wrapper {
    border: 1px solid ${DashboardTheme.gray200};
    border-radius: 10px;
    padding: 4px 10px;
    font-family: ${DashboardTheme.bodyFontFamily};
  }

  .react-date-picker__inputGroup__input {
    color: ${DashboardTheme.navy800};
  }

  .react-date-picker__inputGroup__leadingZero {
    color: ${DashboardTheme.gray700};
  }

  .react-date-picker__calendar-button svg,
  .react-date-picker__clear-button svg {
    stroke: ${DashboardTheme.gray500};
  }
  `,
);

export const compareOfficesPanel: CSSProperties = {
  padding: '16px 20px',
  background: DashboardTheme.white,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: DashboardTheme.radius,
  boxShadow: DashboardTheme.shadow,
  marginBottom: 20,
  minWidth: 0,
};

export const compareOfficesControlsRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  alignItems: 'flex-end',
  marginBottom: 14,
};

export const officeCheckboxList: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
};

export const officeCheckboxRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  color: DashboardTheme.navy800,
  whiteSpace: 'nowrap',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const compareOfficesHint: CSSProperties = {
  fontSize: 11.5,
  color: DashboardTheme.gray500,
  marginTop: 6,
};

// Without an explicit color this inherits the app shell's default body text color (white),
// invisible against the white page background — same class of bug as the threshold-tab fixes.
export const errorMessage: CSSProperties = {
  display: 'block',
  color: DashboardTheme.red500,
  fontSize: 13,
  fontWeight: 600,
  padding: '12px 0',
};

// Hidden by default; only rendered visibly inside the print window/report.
export const printOnly: CSSProperties = {
  display: 'none',
};

/** Heading above a KPI row: "All reservations" / "Group reservations". */
export const kpiSectionTitle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: DashboardTheme.gray700,
  lineHeight: 1.4,
  // Clear space below, so the cards never ride up over the heading.
  margin: '8px 0 10px',
  fontFamily: DashboardTheme.bodyFontFamily,
};

/** "Deleted" after a group name in the per-group table: the group is gone, its history stays. */
export const deletedTag: CSSProperties = {
  marginLeft: 8,
  padding: '1px 6px',
  borderRadius: 10,
  fontSize: 11,
  color: DashboardTheme.gray700,
  background: DashboardTheme.gray100,
  whiteSpace: 'nowrap',
};

/** The " / total" after a group KPI value: smaller and lighter, so the group figure leads. */
export const kpiTotal: CSSProperties = {
  fontSize: '0.6em',
  fontWeight: 500,
  color: DashboardTheme.gray700,
  marginLeft: 2,
};
